// =============================================================================
// LookupListsAdmin.jsx — Manage → "Dropdown Lists"
// =============================================================================
// SuperUser / permitted-admin screen to edit the centralised dropdown lists
// stored in lookup_options (docs/dropdown-lookups-plan.md). One category at a
// time (Customer Type / Sales Order Group / Warehouse): add, rename, edit the
// Unleashed reference, reorder, and deactivate/reactivate values.
//
// Storage is value-string based — deactivating a value only removes it from the
// dropdowns; records that already used it keep the string. `external_ref` holds
// the option's Unleashed identifier (e.g. a Warehouse Code) for the future push.
// =============================================================================
import React from "react";
import {
  deactivateLookupOption,
  insertLookupOption,
  listLookupOptions,
  updateLookupOption,
} from "../../data/lookups";
import { usePageSize } from "../../hooks/usePageSize";
import { alertDialog, confirmDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { LookupSyncModal } from "./LookupSyncModal.jsx";

const CATEGORIES = [
  { key: "customer_type", label: "DFV Service Type/Customer Type" },
  { key: "sales_group", label: "Sales Order Group" },
  { key: "warehouse", label: "Warehouse" },
  { key: "tax", label: "Taxes" },
  // db-207: the Reason type list in the Declined / Needs Info pop-up (reporting only).
  { key: "decision_reason", label: "Decision Reasons" },
];

// Categories that can be pulled from Unleashed — a "Sync from Unleashed" button on
// the tab opens LookupSyncModal (a draggable preview-and-apply modal). Sales Order
// Group has no Unleashed source, so it gets no button.
const SYNC_CONFIG = {
  warehouse: {
    resource: "Warehouses",
    matchBy: "external_ref",
    appVisible: true,
    title: "Warehouses",
  },
  customer_type: {
    resource: "CustomerTypes",
    matchBy: "value",
    appVisible: false,
    title: "Customer Types",
  },
  tax: { resource: "Taxes", matchBy: "external_ref", appVisible: false, title: "Taxes" },
  // Sales Order Groups have no Unleashed reference endpoint — derive the distinct
  // set from the Customers' salesGroup field (additive; see LookupSyncModal).
  sales_group: {
    resource: "Customers",
    matchBy: "value",
    appVisible: true,
    title: "Sales Order Groups",
    deriveField: "salesGroup",
  },
};

// Broad grouping for customer_type values (db-124). Backfill mapped DFVR→DFV
// Refuge and ISO→Intensive Support Org.; First Nation is set by hand.
const OPTION_CATEGORIES = ["DFV Refuge", "Intensive Support Org.", "First Nation", "Other"];

// Rows per page in the management list (John, 31 Jul 2026).
// Rows per page is the shared per-device preference (services/pageSize.ts).

export function LookupListsAdmin() {
  const [PAGE_SIZE] = usePageSize();
  const [cat, setCat] = React.useState("customer_type");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(false);
  const [showHidden, setShowHidden] = React.useState(false);
  const [newValue, setNewValue] = React.useState("");
  const [newRef, setNewRef] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const [editValue, setEditValue] = React.useState("");
  const [editRef, setEditRef] = React.useState("");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [syncOpen, setSyncOpen] = React.useState(false);

  const catLabel = CATEGORIES.find((c) => c.key === cat)?.label || cat;
  // The "Category" column + editor only apply to the customer_type list.
  const showCategory = cat === "customer_type";

  // Reset to the first page when the category or filters change (not on an inline
  // save, which reloads the same list).
  React.useEffect(
    function () {
      setPage(0);
    },
    [cat, showInactive, showHidden]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const load = React.useCallback(
    function () {
      setLoading(true);
      listLookupOptions(cat, { includeInactive: showInactive, includeHidden: showHidden }).then(
        function (data) {
          setRows(data || []);
          setLoading(false);
        }
      );
    },
    [cat, showInactive, showHidden]
  );

  React.useEffect(
    function () {
      load();
    },
    [load]
  );

  function flash(msg) {
    setSavedMsg(msg);
    setTimeout(function () {
      setSavedMsg("");
    }, 2500);
  }

  async function addValue() {
    const v = newValue.trim();
    if (!v) return;
    if (rows.some((r) => !r.deactivated && r.value.toLowerCase() === v.toLowerCase())) {
      await alertDialog({ message: `"${v}" already exists in ${catLabel}.` });
      return;
    }
    const { error } = await insertLookupOption(cat, v, {
      externalRef: newRef.trim() || null,
      sortOrder: rows.length,
    });
    if (error) {
      await alertDialog({ title: "Couldn't add", message: error.message });
      return;
    }
    logAudit("lookup_option_changed", { category: cat, value: v, change: "added" });
    setNewValue("");
    setNewRef("");
    flash("Added");
    load();
  }

  function startEdit(row) {
    setEditingId(row.id);
    setEditValue(row.value);
    setEditRef(row.external_ref || "");
  }

  async function saveEdit(row) {
    const v = editValue.trim();
    if (!v) return;
    const { error } = await updateLookupOption(row.id, {
      value: v,
      external_ref: editRef.trim() || null,
    });
    if (error) {
      await alertDialog({ title: "Couldn't save", message: error.message });
      return;
    }
    logAudit("lookup_option_changed", { category: cat, value: v, change: "renamed" });
    setEditingId(null);
    flash("Saved");
    load();
  }

  // Swap sort_order with the active neighbour in the given direction.
  async function move(row, dir) {
    const active = rows.filter((r) => !r.deactivated);
    const i = active.findIndex((r) => r.id === row.id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= active.length) return;
    const other = active[j];
    await updateLookupOption(row.id, { sort_order: other.sort_order });
    await updateLookupOption(other.id, { sort_order: row.sort_order });
    logAudit("lookup_option_changed", { category: cat, value: row.value, change: "reordered" });
    load();
  }

  async function toggleActive(row) {
    const deactivate = !row.deactivated;
    if (deactivate) {
      const ok = await confirmDialog({
        title: "Deactivate value?",
        message:
          `Remove "${row.value}" from the ${catLabel} dropdown? ` +
          "Records that already use it keep the value; you can reactivate it later.",
        confirmLabel: "Deactivate",
        cancelLabel: "Cancel",
        danger: true,
      });
      if (!ok) return;
    }
    const { error } = await deactivateLookupOption(row.id, deactivate);
    if (error) {
      await alertDialog({ title: "Couldn't update", message: error.message });
      return;
    }
    logAudit("lookup_option_changed", {
      category: cat,
      value: row.value,
      change: deactivate ? "deactivated" : "reactivated",
    });
    flash(deactivate ? "Deactivated" : "Reactivated");
    load();
  }

  // Toggle whether a value appears in the Request-platform dropdowns (app_visible).
  // Unchecking keeps the row for reference/mapping (e.g. an Unleashed code) but
  // removes it from the everyday dropdowns.
  async function toggleVisible(row, visible) {
    const { error } = await updateLookupOption(row.id, { app_visible: visible });
    if (error) {
      await alertDialog({ title: "Couldn't update", message: error.message });
      return;
    }
    logAudit("lookup_option_changed", {
      category: cat,
      value: row.value,
      change: visible ? "shown_in_app" : "hidden_from_app",
    });
    flash("Updated");
    load();
  }

  // Set the broad Category on a customer_type value (db-124). Saves immediately.
  async function setCategory(row, value) {
    const { error } = await updateLookupOption(row.id, { option_category: value || null });
    if (error) {
      await alertDialog({ title: "Couldn't update", message: error.message });
      return;
    }
    logAudit("lookup_option_changed", {
      category: cat,
      value: row.value,
      change: "category_set",
      option_category: value || "",
    });
    flash("Updated");
    load();
  }

  const inputCls =
    "py-[6px] px-[10px] rounded-[7px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <h1 className="m-0 mb-1 text-[22px] text-zephyr-text font-bold">Dropdown Lists</h1>
      <p className="mt-0 mb-4 text-[13px] text-zephyr-muted">
        Manage the values shown in the admin dropdowns. Deactivating a value hides it from new
        entries but keeps it on records that already use it.
      </p>

      {/* Category tabs */}
      <div className="flex gap-2 mb-4">
        {CATEGORIES.map(function (c) {
          const active = c.key === cat;
          return (
            <button
              key={c.key}
              type="button"
              onClick={function () {
                setCat(c.key);
                setEditingId(null);
                setSyncOpen(false);
              }}
              className={`py-[6px] px-[14px] rounded-[8px] text-[13px] font-semibold cursor-pointer border ${
                active
                  ? "bg-zephyr-teal-light border-zephyr-teal text-zephyr-teal-dark"
                  : "bg-white border-zephyr-border text-zephyr-text"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Add row */}
      <div className="flex gap-2 items-center mb-3">
        <input
          className={`${inputCls} w-[260px]`}
          placeholder={`New ${catLabel} value`}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
        />
        <input
          className={`${inputCls} w-[160px]`}
          placeholder="Ref / code (optional)"
          value={newRef}
          onChange={(e) => setNewRef(e.target.value)}
          aria-label="Unleashed reference or code"
        />
        <Btn onClick={addValue}>Add</Btn>
        {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
        {SYNC_CONFIG[cat] && (
          <Btn
            onClick={function () {
              setSyncOpen(true);
            }}
            style={{ marginLeft: "auto" }}
          >
            Sync from Unleashed
          </Btn>
        )}
      </div>

      <label className="flex items-center gap-2 text-[13px] text-zephyr-muted mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        Show deactivated values
      </label>

      <label className="flex items-center gap-2 text-[13px] text-zephyr-muted mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={showHidden}
          onChange={(e) => setShowHidden(e.target.checked)}
        />
        Show all (incl. Unleashed codes not used in the Request platform)
      </label>

      {loading ? (
        <p className="text-zephyr-muted text-[13px]">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-zephyr-muted text-[13px]">No values yet.</p>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-zephyr-off-white text-left">
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  Value
                </th>
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  Ref / code
                </th>
                {showCategory && (
                  <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase w-[180px]">
                    Category
                  </th>
                )}
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  In platform
                </th>
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase w-[220px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(function (row) {
                const editing = editingId === row.id;
                return (
                  <tr
                    key={row.id}
                    className="border-t border-zephyr-border"
                    style={{ opacity: row.deactivated ? 0.5 : 1 }}
                  >
                    <td className="py-[8px] px-[12px]">
                      {editing ? (
                        <input
                          className={`${inputCls} w-[240px]`}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          aria-label="Edit value"
                        />
                      ) : (
                        <span className="text-zephyr-text">
                          {row.value}
                          {row.deactivated && (
                            <span className="ml-2 text-[11px] text-zephyr-muted">
                              (deactivated)
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-[8px] px-[12px]">
                      {editing ? (
                        <input
                          className={`${inputCls} w-[140px]`}
                          value={editRef}
                          onChange={(e) => setEditRef(e.target.value)}
                          aria-label="Edit ref"
                        />
                      ) : (
                        <span className="text-zephyr-muted">{row.external_ref || "-"}</span>
                      )}
                    </td>
                    {showCategory && (
                      <td className="py-[8px] px-[12px]">
                        <select
                          className={`${inputCls} w-[170px]`}
                          value={row.option_category || ""}
                          disabled={row.deactivated}
                          onChange={(e) => setCategory(row, e.target.value)}
                          aria-label={`Category for ${row.value}`}
                        >
                          <option value="">— Select —</option>
                          {OPTION_CATEGORIES.map((oc) => (
                            <option key={oc} value={oc}>
                              {oc}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="py-[8px] px-[12px]">
                      <input
                        type="checkbox"
                        checked={row.app_visible}
                        disabled={row.is_system || row.deactivated}
                        onChange={(e) => toggleVisible(row, e.target.checked)}
                        aria-label={`${row.value} shown in Request platform`}
                      />
                    </td>
                    <td className="py-[8px] px-[12px]">
                      {row.is_system ? (
                        <span className="text-[12px] text-zephyr-muted">System (read-only)</span>
                      ) : editing ? (
                        <div className="flex gap-2">
                          <Btn onClick={() => saveEdit(row)}>Save</Btn>
                          <Btn variant="secondary" onClick={() => setEditingId(null)}>
                            Cancel
                          </Btn>
                        </div>
                      ) : (
                        <div className="flex gap-[6px] items-center">
                          {!row.deactivated && (
                            <>
                              <button
                                type="button"
                                onClick={() => move(row, "up")}
                                aria-label={`Move ${row.value} up`}
                                className="border border-zephyr-border rounded-[6px] px-2 py-[3px] cursor-pointer bg-white text-zephyr-text"
                              >
                                Up
                              </button>
                              <button
                                type="button"
                                onClick={() => move(row, "down")}
                                aria-label={`Move ${row.value} down`}
                                className="border border-zephyr-border rounded-[6px] px-2 py-[3px] cursor-pointer bg-white text-zephyr-text"
                              >
                                Down
                              </button>
                              <button
                                type="button"
                                onClick={() => startEdit(row)}
                                className="border border-zephyr-border rounded-[6px] px-2 py-[3px] cursor-pointer bg-white text-zephyr-text"
                              >
                                Edit
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleActive(row)}
                            className="border border-zephyr-border rounded-[6px] px-2 py-[3px] cursor-pointer bg-white text-zephyr-text"
                          >
                            {row.deactivated ? "Reactivate" : "Deactivate"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Page controls — 20 rows per page (John, 31 Jul 2026). */}
      {!loading && totalPages > 1 && (
        <div className="flex gap-[6px] items-center justify-center mt-4 flex-wrap">
          <button
            type="button"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i).map(function (i) {
            const active = i === safePage;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i)}
                className={`border rounded-[6px] px-[11px] py-[4px] cursor-pointer text-[13px] ${
                  active
                    ? "bg-zephyr-teal-dark border-zephyr-teal-dark text-white font-bold"
                    : "bg-white border-zephyr-border text-zephyr-text"
                }`}
              >
                {i + 1}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage >= totalPages - 1}
            className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {syncOpen && SYNC_CONFIG[cat] && (
        <LookupSyncModal
          resource={SYNC_CONFIG[cat].resource}
          category={cat}
          matchBy={SYNC_CONFIG[cat].matchBy}
          appVisible={SYNC_CONFIG[cat].appVisible}
          title={SYNC_CONFIG[cat].title}
          deriveField={SYNC_CONFIG[cat].deriveField}
          onClose={function () {
            setSyncOpen(false);
          }}
          onApplied={load}
        />
      )}
    </div>
  );
}
